import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import images from "../../assets/images/constantImages";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import icons from "../../assets/images/constantIcons";
import apiService, { handleError } from "./ApiCommon/ApiService";
import { UserContext } from "../Layout/UserContext";

const Login = ({ role }) => {
  const { updateUserData } = useContext(UserContext);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      const userType = localStorage.getItem("userType");
      navigate(
        userType === "vendor"
          ? "/mainDashboard/dashboard"
          : userType === "staff"
            ? "/mainDashboard/staffDashboard"
            : userType === "canteenUser"
              ? "/mainDashboard/dashboardCt"
              : "/mainDashboard/dashboard"
      );
    }

    // remember credentials
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    const rememberedPassword = localStorage.getItem("rememberedPassword");
    if (rememberedEmail && rememberedPassword) {
      formik.setValues({ email: rememberedEmail, password: rememberedPassword });
      setRememberMe(true);
    }
  }, [navigate]);
  // Form validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/\d/, "Password must contain at least one number")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character"
      )
      .required("Password is required"),
  });

  // Handle login
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true)
      try {
        const urls = {
          vendor: "https://headquater.pixoatic.com/api/v1/vendors/login",
          staff: "https://headquater.pixoatic.com/api/v1/staff_users/login",
          canteenUser: "https://headquater.pixoatic.com/api/v1/canteen_users/login"
        };

        // Use the appropriate API based on role
        const response = await axios.post(urls[role], values, {
          headers: { "Content-Type": "application/json" },
        });
        const token = response.data.access_token;
        const userId = response.data.id;
        const userlogo = response.data.store_logo;
        const storeName = response.data.store_name;

        const userName =
          role === "staff"
            ? response.data.full_name
            : response.data.first_name;
        const moduleAccess = response.data.module_access || []; // Ensure it is an array

        if (token) {
          localStorage.setItem("authToken", token);
          localStorage.setItem("userName", userName);
          localStorage.setItem("userType", role);
          localStorage.setItem("userId", userId);
          localStorage.setItem("userlogo", userlogo);
          localStorage.setItem("storeName", storeName);
          window.dispatchEvent(new Event("storageUpdate"));

          if (role === "staff") {
            localStorage.setItem("moduleAccess", JSON.stringify(moduleAccess));
            localStorage.setItem("userId", userId);
          }
          // store credntials if remember me checked
          if (rememberMe) {
            localStorage.setItem("rememberedEmail", values.email);
            localStorage.setItem("rememberedPassword", values.password);
          } else {
            localStorage.removeItem("rememberedEmail");
            localStorage.removeItem("rememberedPassword");
          }
          // update user context
          updateUserData({
            userName,
            userType: role,
            userId: userId.toString(),
          })
          toast.success(response.data.message, { duration: 1000 });
          navigate(
            role === "vendor"
              ? "/mainDashboard/dashboard"
              : role === "staff"
                ? "/mainDashboard/dashboard"
                : role === "canteenUser"
                  ? "/mainDashboard/dashboardCt"
                  : "/mainDashboard/dashboard"
          );
        }
      } catch (errors) {
        handleError(errors);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <>
      <div className="w-full flex relative justify-center items-center">
        <div className="absolute top-0 z-10 w-full">
          <img
            src={images.loginclipimage}
            alt=""
            className="w-full h-screen opacity-[8%]"
          />
        </div>
        <div className="md:w-[50%] md:bg-white flex justify-center items-center md:relative absolute inset-0 z-20 rounded-md">
          <div className="bg-white w-[310px] py-3 px-5 xs:w-[430px] md:w-[400px] lp:w-[450px] flex flex-col">
            <h1 className="text-G800 font-bold text-2xl xs:text-[30px]">
              {role === "staff" ? "Welcome to Staff" : role === "vendor" ? "Welcome to Snooker" : role === "canteenUser" ? "Welcome to Canteen" : "Welcome to Snooker"}
            </h1>

            <p className="text-G200 font-normal text-sm xs:text-[16px] leading-[24px] mb-4 md:mb-5">
              Please sign-in to your account and start the adventure
            </p>
            <form onSubmit={formik.handleSubmit} className="flex flex-col">
              <label className="text-[16px] text-G300 mb-2">Email</label>
              <input
                type="text"
                name="email"
                placeholder="Enter your email"
                {...formik.getFieldProps("email")}
                className="border border-G30 rounded-lg p-3 text-sm outline-none"
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red text-sm">{formik.errors.email}</div>
              )}

              <div className="text-G300 relative">
                <label className="block text-[16px] font-normal mb-2 mt-4">
                  Password
                </label>

                <div className="relative w-full">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter password"
                    {...formik.getFieldProps("password")}
                    className="border w-full border-G30 rounded-lg p-3 text-sm outline-none"
                  />
                  <span
                    className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-[#0D762A]"
                    onClick={() => setShowPassword(!showPassword)} // Toggle state
                  >
                    {showPassword ? (
                      <icons.FaEye className="w-5 h-5" />
                    ) : (
                      <icons.FaEyeSlash className="w-5 h-5" />
                    )}
                  </span>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className="text-red text-sm">
                    {formik.errors.password}
                  </div>
                )}
              </div>

              <div className="flex justify-between text-[16px] mt-2">
                <div className="flex items-end">
                  <input type="checkbox" className="accent-green w-5 h-5" checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)} />
                  <span className="text-G300 font-normal ml-2 text-sm xs:text-base">
                    Remember me
                  </span>
                </div>
                <div>
                  <Link to={`/forgetPassword/${role}`}>
                    <p className="text-green700 font-medium text-sm xs:text-base">
                      Forgot password?
                    </p>
                  </Link>
                </div>
              </div>
              <button
                type="submit"
                className={` font-semibold text-base md:text-lg text-center py-[14px] rounded-lg mt-5 md:mt-10
                  ${isSubmitting ? "bg-green50 text-green cursor-not-allowed" : "bg-green text-white"}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Logging you in..." : "Log In"}
              </button>
            </form>
          </div>
        </div>
        {/* canteenloginimage */}
        <div className="w-full md:w-[50%] bg-green relative flex justify-center items-center">
          {role === "canteenUser" ?
            <img
              src={images.canteenloginimage}
              alt="canteenloginimage"
              className="w-full h-screen"
            /> :
            <img
              src={images.loginimage}
              alt="loginimage"
              className="w-full h-screen backdrop-blur-[8px] mix-blend-multiply"
            />
          }
          <div className="absolute right-2 sm:right-10 md:right-auto top-3 md:top-1/3 xl:top-[36%]">
            <img
              src={images.logo}
              alt="logo"
              className="w-[180px] h-[75px] md:w-[300px] md:h-[130px] lg:w-[350px] xl:w-[600px] xl:h-[200px]"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
